import { defineStore } from 'pinia';
import { reactive } from 'vue';

import AcordoExternoDto from '@/models/dto/AcordoExternoDto';
import { StatusProposta } from '@/models/enums';
import { PropostaNovoResource } from '@/models/resources';
import AcordoExternoResource from '@/models/resources/AcordoExternoResource';

/**
 * Status que alguma seção da aba de acordos exibe. Credor só com proposta ou acordo externo fora
 * desta lista (recusada, quebra de acordo, erro) não conta como "com proposta": senão a aba fica
 * vazia em vez de mostrar o card de sem acordo.
 */
const STATUS_PROPOSTA_EXIBIVEL: ReadonlySet<StatusProposta> = new Set([
  StatusProposta.Aceita,
  StatusProposta.PendenteDeRetorno,
  StatusProposta.EmAnalise,
  StatusProposta.Quitada,
]);

const usePropostasStore = defineStore('propostas', () => {
  const acordosExternosPorCredor = reactive<Record<string, AcordoExternoResource[]>>({});
  const propostasPorCredor = reactive<Record<string, PropostaNovoResource[]>>({});

  // #region: Propostas
  function listarPropostasPorCredor(api: string): PropostaNovoResource[] {
    return propostasPorCredor[api] ?? [];
  }

  function listarPropostasPorStatus(statuses: Set<StatusProposta>): PropostaNovoResource[] {
    const retorno: PropostaNovoResource[] = [];
    Object.values(propostasPorCredor).forEach((propostas) => {
      propostas.forEach((proposta) => {
        if (statuses.has(proposta.status)) {
          retorno.push(proposta);
        }
      });
    });

    return retorno;
  }

  function listarPropostas(): PropostaNovoResource[] {
    const retorno: PropostaNovoResource[] = [];
    Object.values(propostasPorCredor).forEach((propostas) => {
      propostas.forEach((proposta) => {
        retorno.push(proposta);
      });
    });

    return retorno;
  }

  function listarCredoresComProposta(): string[] {
    const retorno: Set<string> = new Set();
    Object.keys(propostasPorCredor).forEach((api) => {
      if (propostasPorCredor[api]?.some((proposta) => STATUS_PROPOSTA_EXIBIVEL.has(proposta.status))) {
        retorno.add(api);
      }
    });
    Object.keys(acordosExternosPorCredor).forEach((api) => {
      if (acordosExternosPorCredor[api]?.some((acordo) => STATUS_PROPOSTA_EXIBIVEL.has(acordo.status))) {
        retorno.add(api);
      }
    });

    return Array.from(retorno);
  }

  function obterPropostaPorId(
    api: string,
    propostaId: number,
  ): PropostaNovoResource | undefined {
    return propostasPorCredor[api]?.find((p) => p.id === propostaId);
  }

  function atualizarTodasPropostas(api: string, propostasAtuais: PropostaNovoResource[]) {
    propostasPorCredor[api] ??= [];
    propostasPorCredor[api].length = 0;
    propostasPorCredor[api].push(...propostasAtuais);
  }

  function atualizarProposta(api: string, propostaAtual: PropostaNovoResource) {
    propostasPorCredor[api] ??= [];
    const index = propostasPorCredor[api].findIndex((p) => p.id === propostaAtual.id);
    if (index !== -1) {
      propostasPorCredor[api][index] = propostaAtual;
    } else {
      propostasPorCredor[api].push(propostaAtual);
    }
  }

  // #endregion: Propostas

  // #region: Acordos Externos
  function listarAcordosExternosPorCredor(api: string): AcordoExternoResource[] {
    return acordosExternosPorCredor[api] ?? [];
  }

  function obterAcordoExternoPorId(
    api: string,
    acordoExternoId: number,
  ): AcordoExternoResource | undefined {
    return acordosExternosPorCredor[api]?.find((p) => p.id === acordoExternoId);
  }

  async function atualizarTodosAcordosExternos(
    api: string,
    acordosExternosAtuais: AcordoExternoResource[],
  ): Promise<void> {
    acordosExternosPorCredor[api] ??= [];
    acordosExternosPorCredor[api].length = 0;
    acordosExternosPorCredor[api].push(...acordosExternosAtuais);
  }

  function atualizarAcordoExterno(api: string, acordoAtual: AcordoExternoDto) {
    acordosExternosPorCredor[api] ??= [];
    const index = acordosExternosPorCredor[api].findIndex((p) => p.id === acordoAtual.id);
    if (index !== -1) {
      acordosExternosPorCredor[api][index] = new AcordoExternoResource(api, acordoAtual);
    } else {
      acordosExternosPorCredor[api].push(new AcordoExternoResource(api, acordoAtual));
    }
  }

  // #endregion: Acordos Externos

  function limpar(api: string) {
    delete propostasPorCredor[api];
    delete acordosExternosPorCredor[api];
  }

  function limparTodosCredores() {
    Object.keys(propostasPorCredor).forEach((api) => {
      limpar(api);
    });
    Object.keys(acordosExternosPorCredor).forEach((api) => {
      limpar(api);
    });
  }

  return {
    listarPropostas,
    listarPropostasPorStatus,
    listarPropostasPorCredor,
    obterPropostaPorId,
    atualizarTodasPropostas,
    atualizarProposta,

    listarAcordosExternosPorCredor,
    obterAcordoExternoPorId,
    atualizarTodosAcordosExternos,
    atualizarAcordoExterno,

    limpar,
    limparTodosCredores,

    propostasPorCredor,
    acordosExternosPorCredor,

    listarCredoresComProposta,
  };
});

export default usePropostasStore;
